import { Button } from "@town/ui";
import { TOWN_DEFAULT_OFFICE_TIME } from "@town/contracts";
import { LandingOffice } from "../landing/LandingOffice";
import { LANDING_LOOKS } from "../landing/landingLooks";
import { useMinute } from "../lib/minute-clock";
import { drawCharacterPreview } from "../pixel/characterSprites";
import { townSky } from "../scene/sky";
import "./landing-page.css";

/** Demo images in public/promo, captured from the demo office by scripts/capture-promo.ts. */
export const LANDING_FEATURES = [
  { image: "talk", title: "Walk over to talk", text: "Stand next to someone and you can hear each other. Walk away and the call ends." },
  { image: "meeting", title: "Meet in a room", text: "Step into a meeting room and the lights come on. Only the people inside hear the call." },
  { image: "late", title: "Real day and night", text: "The office follows the sun in your city. After dark, lamps and busy desks light the floor." },
] as const;

const officeClock = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: TOWN_DEFAULT_OFFICE_TIME.timeZone });

/** How dark the office is right now, by Ho Chi Minh City's sun, and its local time. */
function useOfficeSky(): { night: number; time: string } {
  const minute = useMinute();
  const instant = new Date(minute * 60_000);
  return { night: townSky(instant, TOWN_DEFAULT_OFFICE_TIME).night, time: officeClock.format(instant) };
}

function Brand() {
  return (
    <div className="landing-brand">
      <span className="town-mark"><span>LL</span><i /></span>
      <span><strong>LittleLives</strong><small>Town</small></span>
    </div>
  );
}

export function LandingPage({ onEnter }: { onEnter: () => void }) {
  const { night, time } = useOfficeSky();
  return (
    <main className="town-landing">
      <header className="landing-nav">
        <Brand />
        <Button size="sm" variant="secondary" onClick={onEnter}>Sign in</Button>
      </header>

      <section className="landing-stage" aria-labelledby="landing-title">
        <LandingOffice
          onEnter={onEnter}
          night={night}
          cover={(
            <div className="landing-hero">
              <p className="landing-kicker">
                <span className="landing-live" aria-hidden="true" />
                Live from Ho Chi Minh City · {time}
              </p>
              <h1 id="landing-title">
                <span>Your office,</span>{" "}
                <span>one <em>walk</em> away.</span>
              </h1>
              <p className="landing-lede">Walk over to someone to talk, or step into a room to meet in private. The office below is a live demo. Go say hi.</p>
              <div className="landing-actions">
                <Button size="lg" variant="primary" onClick={onEnter}>Enter your office</Button>
                <span className="landing-or">or walk through the door →</span>
              </div>
            </div>
          )}
        />
      </section>

      <section className="landing-features" aria-labelledby="landing-features-title">
        <h2 id="landing-features-title" className="landing-section-title">How the office works</h2>
        {LANDING_FEATURES.map((feature, index) => (
          <article key={feature.image} className="landing-feature">
            <figure>
              <img src={`/promo/${feature.image}.webp`} alt="" width={960} height={640} loading="lazy" decoding="async" />
            </figure>
            <div>
              <span className="landing-feature-number">0{index + 1}</span>
              <h3>{feature.title}</h3>
              <p>{feature.text}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="landing-finale" aria-labelledby="landing-finale-title">
        <div className="landing-crowd" aria-hidden="true">
          {LANDING_LOOKS.map((appearance, index) => (
            <canvas key={index} style={{ animationDelay: `${index * 90}ms` }} ref={(canvas) => { if (canvas) drawCharacterPreview(canvas, appearance, index, 4); }} />
          ))}
        </div>
        <h2 id="landing-finale-title">Your desk is ready.</h2>
        <p>Up to 40 people per office. Bring the team.</p>
        <Button size="lg" variant="primary" onClick={onEnter}>Enter your office</Button>
      </section>

      <footer className="landing-footer">
        <Brand />
        <Button size="sm" variant="ghost" onClick={onEnter}>Sign in</Button>
      </footer>
    </main>
  );
}
